import {
	AfterViewInit,
	Component,
	ElementRef,
	OnInit,
	input,
	output,
	viewChild
} from "@angular/core";
import { map, debounceTime, distinctUntilChanged } from "rxjs/operators";
import { fromEvent as observableFromEvent } from "rxjs";

import { MatPaginator, MatPaginatorIntl, PageEvent } from "@angular/material/paginator";
import { TmMatPaginatorInit } from "libs/material/src/lib/services/mat-paginator.init";
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { TranslatePipe } from "@ngx-translate/core";
import { DashboardState } from "libs/treatments/src/lib/store/treatment.reducer";
import { DashboardEntriesDatasource } from "libs/treatments/src/lib/services";
import { MatTableDataSource } from "@angular/material/table";
import { PatientModel } from "libs/tm-common/src/lib/models/patient.model";
import { FileListDashboardEntriesDatasource } from "libs/media/src/lib/services/file-list-dashboard.datasource";
import { MatOption, MatSelect } from "@angular/material/select";
import {
	FUNCTIONAL_PRODUCT_KEYS,
	MEMOTAIN_PRODUCT_KEYS,
	PEEK_RETAINER_PRODUCT_KEYS,
	ProductKey,
	PROFESSIONAL_CASE_ASSESSMENT_PRODUCT_KEYS,
	PROFESSIONAL_PRODUCT_KEYS,
	RETENTIONSSCHIENE_PRODUCT_DATA_KEYS
} from "libs/product/src/lib/models/product.model";
import { MatIconModule } from "@angular/material/icon";
import { MatIconButton } from "@angular/material/button";
import { NgStyle } from "@angular/common";

@Component({
	selector: "lib-grid-header",
	templateUrl: "./grid-header.component.html",
	styleUrls: ["./grid-header.component.scss"],
	providers: [{ provide: MatPaginatorIntl, useClass: TmMatPaginatorInit }],
	imports: [
		MatFormField,
		MatPrefix,
		MatInput,
		MatSuffix,
		MatLabel,
		MatSelect,
		MatOption,
		MatIconModule,
		MatIconButton,
		MatPaginator,
		NgStyle,
		TranslatePipe
	]
})
export class GridHeaderComponent implements OnInit, AfterViewInit {
	readonly dataSource = input<
		| DashboardEntriesDatasource
		| MatTableDataSource<PatientModel>
		| FileListDashboardEntriesDatasource
	>(undefined);
	readonly gridState = input<DashboardState>(undefined); // XXX represents the current 'DashboardState' (see treatments -> dashboardStates in redux store)
	readonly isProductFilterVisible = input<boolean>(false);
	readonly stateChange = output<object>();

	readonly filter = viewChild<ElementRef>("filter");
	readonly paginator = viewChild(MatPaginator);

	ProductKey = ProductKey;
	productFilter = input<string[]>([]);

	// pagination properties
	paginationProps = {
		length: 500,
		pageSize: 6,
		pageIndex: 0,
		pageEvent: PageEvent,
		pageSizeOptions: [6, 12, 18, 24, 36]
	};

	// XXX this object holds information about the currently set pagination options and
	// the content of the "search" textfilter
	currentPaginationAndFilterState: {
		filter?: string;
		length?: number;
		pageIndex?: number;
		pageSize?: number;
		previousPageIndex?: number;
	} = {};

	readonly OTHER_PRODUCTS_FILTER_KEY = "other";

	constructor() {}
	ngOnInit() {
		this.dataSource().paginator = this.paginator();
	}

	ngAfterViewInit(): void {
		// watch filter changes
		if (this.filter()) {
			observableFromEvent(this.filter().nativeElement, "keyup")
				.pipe(
					debounceTime(200), // 350 was too long if someone types fast
					map(() => this.filter().nativeElement.value),
					distinctUntilChanged()
				)
				.subscribe((filter) => this.onFilterChange(filter));
		}
		// Overwrite current pagination properties with values from dashboard state from redux store
		this.paginationProps = { ...this.paginationProps, ...this.gridState() };

		const pageEvent = {
			pageIndex: this.paginationProps.pageIndex,
			pageSize: this.paginator().pageSize,
			length: this.paginator().length
		};
		this.paginator().pageIndex = this.paginationProps.pageIndex;
		this.paginator().page.emit(pageEvent);
	}

	onFilterChange(filter) {
		this.currentPaginationAndFilterState = { ...this.currentPaginationAndFilterState, filter };
		//inform parent about change in filter
		this.stateChange.emit(this.currentPaginationAndFilterState);
	}

	onPageEvent($pageEvent: PageEvent) {
		this.currentPaginationAndFilterState = {
			...this.currentPaginationAndFilterState,
			...$pageEvent
		};
		//inform parent about change in pagination
		this.stateChange.emit($pageEvent);
	}

	onProductSelection(productFilter: string[]) {
		const expandedFilter: string[] = [];

		const isProfessionalSelected = productFilter.includes(
			ProductKey.PRO_SMILE_SCHIENE.toString()
		);
		const isFunctionALSelected = productFilter.includes(ProductKey.FUNCTIONAL_SCHIENE_NEO.toString());
		const isMemotainSelected = productFilter.includes(ProductKey.MEMOTAIN.toString());
		const isPeekRetainerSelected = productFilter.includes(ProductKey.PEEK_RETAINER.toString());
		const isRetentionsschieneSelected = productFilter.includes(
			ProductKey.RETENTIONSSCHIENE.toString()
		);
		const isCaseAssessmentSelected = productFilter.includes(
			ProductKey.PRO_CASE_ASSESSMENT_SCHIENE.toString()
		);
		const isOtherSelected = productFilter.includes(this.OTHER_PRODUCTS_FILTER_KEY);

		// add all product keys of the related category to the filter
		if (isProfessionalSelected) {
			expandedFilter.push(
				...Object.values(PROFESSIONAL_PRODUCT_KEYS).map((key) => key.toString())
			);
		}
		if (isFunctionALSelected) {
			expandedFilter.push(
				...Object.values([...FUNCTIONAL_PRODUCT_KEYS, ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT]).map((key) => key.toString())
			);
		}
		if (isMemotainSelected) {
			expandedFilter.push(
				...Object.values(MEMOTAIN_PRODUCT_KEYS).map((key) => key.toString())
			);
		}
		if (isPeekRetainerSelected) {
			expandedFilter.push(
				...Object.values(PEEK_RETAINER_PRODUCT_KEYS).map((key) => key.toString())
			);
		}
		if (isRetentionsschieneSelected) {
			expandedFilter.push(
				...Object.values(RETENTIONSSCHIENE_PRODUCT_DATA_KEYS).map((key) => key.toString())
			);
		}
		if (isCaseAssessmentSelected) {
			expandedFilter.push(
				...Object.values(PROFESSIONAL_CASE_ASSESSMENT_PRODUCT_KEYS).map((key) =>
					key.toString()
				)
			);
		}
		if (isOtherSelected) {
			expandedFilter.push(...this.otherProductKeys);
		}

		// add all non-array filters (individual product selections e.g PEEK RETAINER)
		expandedFilter.push(...productFilter);

		//remove duplicates
		const expandedFilterSet = [...new Set(expandedFilter)];

		this.currentPaginationAndFilterState = {
			...this.currentPaginationAndFilterState,
			...{ productFilter: expandedFilterSet }
		};
		//inform parent about change in product filter
		this.stateChange.emit(this.currentPaginationAndFilterState);
	}

	get otherProductKeys() {
		const otherProductKeys = (
			this.dataSource() as DashboardEntriesDatasource
		).uniqueProductKeys.filter(
			(key) =>
				!Object.values(PROFESSIONAL_PRODUCT_KEYS).includes(key) &&
				!Object.values([...FUNCTIONAL_PRODUCT_KEYS, ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT]).includes(key) &&
				!Object.values(MEMOTAIN_PRODUCT_KEYS).includes(key) &&
				!Object.values(RETENTIONSSCHIENE_PRODUCT_DATA_KEYS).includes(key) &&
				!Object.values(PROFESSIONAL_CASE_ASSESSMENT_PRODUCT_KEYS).includes(key) &&
				!Object.values(PEEK_RETAINER_PRODUCT_KEYS).includes(key)
		);
		return otherProductKeys.map((key) => key.toString());
	}

	get _dataSource() {
		return this.dataSource() as DashboardEntriesDatasource;
	}

	hasProfessionalTreatment() {
		return this._dataSource.uniqueProductKeys.some((key) =>
			Object.values(PROFESSIONAL_PRODUCT_KEYS).includes(key)
		);
	}
	hasFunctionALTreatment() {
		return this._dataSource.uniqueProductKeys.some((key) =>
			Object.values([...FUNCTIONAL_PRODUCT_KEYS, ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT]).includes(key)
		);
	}
	hasMemotainTreatment() {
		return this._dataSource.uniqueProductKeys.some((key) =>
			Object.values(MEMOTAIN_PRODUCT_KEYS).includes(key)
		);
	}
	hasRetentionsschieneTreatment() {
		return this._dataSource.uniqueProductKeys.some((key) =>
			Object.values(RETENTIONSSCHIENE_PRODUCT_DATA_KEYS).includes(key)
		);
	}
	hasCaseAssessmentTreatment() {
		return this._dataSource.uniqueProductKeys.some((key) =>
			Object.values(PROFESSIONAL_CASE_ASSESSMENT_PRODUCT_KEYS).includes(key)
		);
	}
	hasPeekRetainerTreatment() {
		return this._dataSource.uniqueProductKeys.some((key) =>
			Object.values(PEEK_RETAINER_PRODUCT_KEYS).includes(key)
		);
	}
}
